import type { CaptureResult, PostHogConfig } from 'posthog-js'

/**
 * PostHog init policy, kept pure and runtime-agnostic so it can be unit
 * tested in Node.js and reused by the provider.
 */

export type PostHogPublicConfig = {
  /** Public PostHog host; must be a valid https:// URL. */
  host?: string
  /** Public PostHog project token (safe to expose to browsers). */
  token?: string
}

export const isValidPostHogHost = (host?: string): boolean => {
  if (!host) return false

  try {
    return new URL(host).protocol === 'https:'
  } catch {
    return false
  }
}

export type ValidPostHogPublicConfig = {
  host: string
  token: string
}

/**
 * Init requires both a token and a valid HTTPS host. Missing or malformed
 * config means no initialization and no network traffic (local/test/E2E).
 */
export const shouldInitPostHog = (
  config: PostHogPublicConfig,
): config is ValidPostHogPublicConfig =>
  Boolean(config.token?.trim()) && isValidPostHogHost(config.host)

/**
 * Final outbound policy. There are currently no approved business events or
 * feature-flag consumers, so the event allowlist is intentionally empty.
 * Property allowlists belong to an approved event and cannot enable an event
 * on their own.
 */
const OUTBOUND_EVENT_PROPERTY_ALLOWLIST = new Map<string, ReadonlySet<string>>()

/**
 * Applies the final event/property allowlist immediately before capture. An
 * unapproved event is dropped rather than attempting to enumerate sensitive
 * property names. For an approved event, only its explicitly approved scalar
 * properties survive; person-property payloads and all other top-level values
 * are discarded.
 */
export const sanitizePostHogEvent = (event: CaptureResult | null): CaptureResult | null => {
  if (!event) return null

  const approvedProperties = OUTBOUND_EVENT_PROPERTY_ALLOWLIST.get(event.event)

  if (!approvedProperties) return null

  const properties = Object.fromEntries(
    Object.entries(event.properties).filter(
      ([key, value]) =>
        approvedProperties.has(key) &&
        (typeof value === 'boolean' || typeof value === 'number' || typeof value === 'string'),
    ),
  )

  return {
    event: event.event,
    properties,
    timestamp: event.timestamp,
    uuid: event.uuid,
  }
}

/**
 * Privacy-safe init options:
 * - no autocapture, pageview, or pageleave capture
 * - no session recording
 * - no person profiles (`person_profiles: 'never'`)
 * - in-memory persistence only: no durable identity survives a reload
 */
export const getPostHogInitOptions = (config: PostHogPublicConfig): Partial<PostHogConfig> => ({
  advanced_disable_flags: true,
  api_host: config.host,
  autocapture: false,
  before_send: event => sanitizePostHogEvent(event),
  capture_pageleave: false,
  capture_pageview: false,
  capture_performance: false,
  disable_capture_url_hashes: true,
  disable_conversations: true,
  disable_external_dependency_loading: true,
  disable_product_tours: true,
  disable_session_recording: true,
  disable_surveys: true,
  disable_web_experiments: true,
  disableDeviceModel: true,
  persistence: 'memory',
  person_profiles: 'never',
  rageclick: false,
  save_campaign_params: false,
  save_referrer: false,
})
